/**
 * Live mode — what is playing now, and what is next (plan.md §54, Phase 2).
 *
 * In `shared/` rather than in a screen because three places need the same
 * answer and must not disagree: the visitor app, a future native app, and any
 * "on now" summary the API ends up serving. It is also the part with the
 * fiddly rules, and those are worth writing once.
 *
 * Pure, like everything here — `now` is an argument. That is what lets a test
 * stand at 23:59 on the busiest night of the festival without touching the
 * system clock.
 */
import type { TimeZone } from './time.js';
import { dayBucket } from './time.js';

/** The minimum an act needs for live mode to place it. */
export interface LiveAct {
  readonly slug: string;
  readonly stageId: string;
  readonly startAt: string | Date;
  readonly endAt: string | Date;
}

const at = (value: string | Date): Date => (value instanceof Date ? value : new Date(value));

/**
 * One stage's answer, which is the unit that matters.
 *
 * A festival with 24 stages has no single "playing now" — someone standing in
 * a field wants to know what is on at each of them, and a flat list of 24 acts
 * with no structure is the same question left unanswered.
 */
export interface StageNow<T extends LiveAct = LiveAct> {
  stageId: string;
  /** Playing at `now`. Null between sets. */
  now: T | null;
  /** The next act to start on this stage, if there is one today. */
  next: T | null;
  /** Milliseconds until `next` starts. Null when there is no next. */
  untilNextMs: number | null;
}

/**
 * Half-open, exactly as clash detection is: an act ending at 22:00 is NOT
 * playing at 22:00, and the one starting then is. Getting this wrong shows two
 * acts on one stage at every changeover.
 */
export function isPlayingAt<T extends LiveAct>(act: T, now: Date): boolean {
  const start = at(act.startAt).getTime();
  const end = at(act.endAt).getTime();
  const t = now.getTime();
  return start <= t && t < end;
}

/**
 * What is on, per stage, at this instant.
 *
 * Stages with nothing on and nothing coming are omitted — a live screen listing
 * eighteen empty stages buries the six that are playing.
 */
export function liveByStage<T extends LiveAct>(
  acts: readonly T[],
  now: Date,
  options: { lookaheadMs?: number } = {},
): StageNow<T>[] {
  // A default of six hours rather than "the rest of the day": at 03:00 the
  // interesting answer is what is on before sunrise, not something at noon.
  const lookaheadMs = options.lookaheadMs ?? 6 * 60 * 60_000;
  const horizon = now.getTime() + lookaheadMs;

  const byStage = new Map<string, StageNow<T>>();

  for (const act of acts) {
    const start = at(act.startAt).getTime();
    const entry = byStage.get(act.stageId) ?? {
      stageId: act.stageId, now: null, next: null, untilNextMs: null,
    };

    if (isPlayingAt(act, now)) {
      // A stage that somehow has two acts overlapping shows the one that
      // started most recently — the later one is what a visitor walking up
      // would find playing.
      if (!entry.now || at(entry.now.startAt).getTime() < start) entry.now = act;
    } else if (start >= now.getTime() && start <= horizon) {
      if (!entry.next || start < at(entry.next.startAt).getTime()) {
        entry.next = act;
        entry.untilNextMs = start - now.getTime();
      }
    }

    byStage.set(act.stageId, entry);
  }

  return [...byStage.values()]
    .filter((entry) => entry.now || entry.next)
    // Playing first, then by how soon: the field a visitor scans top-down
    // should answer "where do I go right now" before "what should I plan for".
    .sort((a, b) => {
      if (Boolean(a.now) !== Boolean(b.now)) return a.now ? -1 : 1;
      return (a.untilNextMs ?? Infinity) - (b.untilNextMs ?? Infinity);
    });
}

/** Everything playing at this instant, across every stage. */
export function playingNow<T extends LiveAct>(acts: readonly T[], now: Date): T[] {
  return acts.filter((act) => isPlayingAt(act, now));
}

/**
 * Of a visitor's own plan, what is on now and what they should be walking to.
 *
 * The single most useful thing live mode does. `startingSoon` is what turns it
 * from a status display into something that gets someone out of a hammock.
 */
export interface MyLive<T extends LiveAct = LiveAct> {
  now: T[];
  /** Saved acts starting within the warning window, soonest first. */
  startingSoon: T[];
}

export function myLive<T extends LiveAct>(
  acts: readonly T[],
  saved: ReadonlySet<string>,
  now: Date,
  options: { soonMs?: number } = {},
): MyLive<T> {
  // Twenty minutes: long enough to cross a site the size of OZORA, short
  // enough that "soon" still means soon.
  const soonMs = options.soonMs ?? 20 * 60_000;
  const mine = acts.filter((act) => saved.has(act.slug));

  return {
    now: mine.filter((act) => isPlayingAt(act, now)),
    startingSoon: mine
      .filter((act) => {
        const delta = at(act.startAt).getTime() - now.getTime();
        return delta > 0 && delta <= soonMs;
      })
      .sort((a, b) => at(a.startAt).getTime() - at(b.startAt).getTime()),
  };
}

/**
 * The day the app should be showing.
 *
 * Not today's date. At 02:00 on 31 July a visitor is still at the 30th's
 * night, and opening the app to a schedule that has already ended is the
 * single most confusing thing a festival app can do. The rule matches the
 * midnight bucket: an act starting 00:30 belongs to the 31st, so the 31st is
 * what should be on screen — but only once something on it has started.
 */
export function liveDay<T extends LiveAct>(
  acts: readonly T[],
  now: Date,
  timeZone: TimeZone,
): string | null {
  if (acts.length === 0) return null;

  const started = acts
    .filter((act) => at(act.startAt).getTime() <= now.getTime())
    .sort((a, b) => at(b.startAt).getTime() - at(a.startAt).getTime());

  // Before the first act of the festival, the first day is what to show.
  if (started.length === 0) {
    const first = [...acts].sort(
      (a, b) => at(a.startAt).getTime() - at(b.startAt).getTime(),
    )[0]!;
    return dayBucket(at(first.startAt), timeZone);
  }
  return dayBucket(at(started[0]!.startAt), timeZone);
}
